import { useRef, useState } from 'react';
import { FileUp, UploadCloud, X } from 'lucide-react';

export const PUBLIC_UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic';
export const PUBLIC_UPLOAD_MAX_BYTES = 25_000_000;

const ACCEPTED_MIME_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/heic']);
const ACCEPTED_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'heic']);

const fileKey = (file) => `${file.name}:${file.size}:${file.lastModified}`;
const fileExtension = (file) => file.name.split('.').pop()?.toLowerCase() || '';
const formatSize = (bytes) => bytes >= 1024 * 1024
  ? `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`
  : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export function PublicFileDropzone({
  id,
  files,
  onFilesChange,
  label,
  lang = 'it',
  maxFiles = 5,
  maxBytes = PUBLIC_UPLOAD_MAX_BYTES,
  disabled = false,
  dark = false,
  testId,
  dropzoneTestId,
  onValidationError,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [validationError, setValidationError] = useState('');
  const en = lang === 'en';
  const maximum = Math.round(maxBytes / 1_000_000);

  const reportError = (message) => {
    setValidationError(message);
    onValidationError?.(message);
  };

  const addFiles = (selectedFiles) => {
    const selected = Array.from(selectedFiles || []);
    if (!selected.length) return;

    const invalidType = selected.find((file) => !ACCEPTED_MIME_TYPES.has(file.type) || !ACCEPTED_EXTENSIONS.has(fileExtension(file)));
    if (invalidType) {
      reportError(en ? 'Only PDF, JPG, PNG or HEIC files are accepted.' : 'Sono accettati solo file PDF, JPG, PNG o HEIC.');
      return;
    }

    const emptyFile = selected.find((file) => file.size === 0);
    if (emptyFile) {
      reportError(en ? `${emptyFile.name} is empty.` : `${emptyFile.name} è vuoto.`);
      return;
    }

    const oversized = selected.find((file) => file.size > maxBytes);
    if (oversized) {
      reportError(en ? `${oversized.name} exceeds the ${maximum} MB limit.` : `${oversized.name} supera il limite di ${maximum} MB.`);
      return;
    }

    const known = new Set(files.map(fileKey));
    const unique = selected.filter((file) => !known.has(fileKey(file)));
    const next = [...files, ...unique];
    if (next.length > maxFiles) {
      reportError(en ? `You can attach up to ${maxFiles} ${maxFiles === 1 ? 'file' : 'files'}.` : `Puoi allegare al massimo ${maxFiles} ${maxFiles === 1 ? 'file' : 'file'}.`);
      return;
    }

    setValidationError('');
    onValidationError?.('');
    onFilesChange(next);
  };

  const onInput = (event) => {
    addFiles(event.target.files);
    event.target.value = '';
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) addFiles(event.dataTransfer.files);
  };

  const removeFile = (file) => {
    onFilesChange(files.filter((item) => item !== file));
    setValidationError('');
    onValidationError?.('');
  };

  return (
    <div>
      {label && <label htmlFor={id} className={`mb-2 block text-sm font-semibold ${dark ? 'text-white/85' : ''}`}>{label}</label>}
      <div
        className={`rounded-xl border-2 border-dashed p-5 text-center transition-colors ${
          dragging
            ? dark ? 'border-brand-light bg-white/10' : 'border-primary bg-brand-pale'
            : dark ? 'border-white/25 bg-white/5' : 'border-primary/40 bg-brand-pale/40'
        } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        onDragEnter={(event) => { event.preventDefault(); if (!disabled) setDragging(true); }}
        onDragOver={(event) => { event.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
        onDrop={onDrop}
        data-testid={dropzoneTestId || (testId ? `${testId}-dropzone` : undefined)}
      >
        <UploadCloud className={`mx-auto h-8 w-8 ${dark ? 'text-brand-light' : 'text-primary'}`} aria-hidden="true" />
        <p className={`mt-3 text-sm font-semibold ${dark ? 'text-white' : ''}`}>
          {en ? 'Drag PDFs or images here' : 'Trascina qui PDF o immagini'}
        </p>
        <p className={`mt-1 text-xs ${dark ? 'text-white/55' : 'text-muted-foreground'}`}>
          {en ? 'or choose them from your device' : 'oppure selezionali dal dispositivo'}
        </p>
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={PUBLIC_UPLOAD_ACCEPT}
          multiple={maxFiles > 1}
          disabled={disabled}
          onChange={onInput}
          className="sr-only"
          data-testid={testId}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className={`mt-4 inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
            dark ? 'border-white/25 bg-white/10 text-white hover:bg-white/15' : 'border-border bg-white text-foreground hover:border-primary hover:text-primary'
          }`}
        >
          <FileUp className="h-4 w-4" aria-hidden="true" />
          {en ? 'Select files' : 'Seleziona file'}
        </button>
        <p className={`mt-3 text-[11px] leading-relaxed ${dark ? 'text-white/55' : 'text-muted-foreground'}`}>
          PDF, JPG, PNG, HEIC · {en ? `up to ${maximum} MB per file` : `fino a ${maximum} MB per file`} · {en ? `maximum ${maxFiles} ${maxFiles === 1 ? 'file' : 'files'}` : `massimo ${maxFiles} file`}
        </p>
      </div>

      {validationError && <p role="alert" className={`mt-2 text-xs ${dark ? 'text-amber-200' : 'text-destructive'}`}>{validationError}</p>}

      {files.length > 0 && (
        <ul className="mt-3 space-y-2" aria-live="polite">
          {files.map((file) => (
            <li key={fileKey(file)} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-xs ${dark ? 'border-white/20 bg-white/10 text-white/85' : 'border-border bg-white'}`}>
              <span className="min-w-0 truncate">{file.name} <span className={dark ? 'text-white/50' : 'text-muted-foreground'}>· {formatSize(file.size)}</span></span>
              <button type="button" aria-label={`${en ? 'Remove' : 'Rimuovi'} ${file.name}`} onClick={() => removeFile(file)} className={`shrink-0 ${dark ? 'text-white/55 hover:text-white' : 'text-muted-foreground hover:text-destructive'}`}>
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
